import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { StyledBase } from './Base';
import { Search } from '../icon/icons/Search';
import { Loader } from '../Loader';
import { ErrorMessage } from './ErrorMessage';

const StyledAutoComplete = styled.div`
  ${({ theme: { layers, media } }) => css`
    position: relative;
    top: 0;
    z-index: ${layers.overlay};
    flex-grow: 1;
    height: 100%;

    @media (min-width: ${media.md}) {
      flex-grow: 0;
      height: 100%;
    }
  `}
`;

const StyledLoader = styled.div`
  ${({ theme: { space } }) => css`
    padding: ${space.xl};
  `}
`;

const StyledSearchIcon = styled(Search)`
  ${({ theme: { space, layers } }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${space.md};
    z-index: ${layers.searchIcon};
  `}
`;

const StyledInput = styled(StyledBase)`
  ${({ noIcon }) => css`
    ${!noIcon &&
    css`
      padding-left: 42px;
    `}
    height: 100%;
  `}
  &::-webkit-search-decoration {
    -webkit-appearance: none;
  }
`;
const StyledSuggestions = styled.div`
  ${({ theme: { colors, maxMedia, space, shadows } }) => css`
    width: 100%;
    margin: 0;
    position: absolute;
    top: 100%;
    background: ${colors.white.default};
    box-shadow: ${shadows.small};

  @media (max-width: ${maxMedia.md}) {
    width calc(100vw - ${space.lg});
    padding: ${space.sm};
  }

  `}
`;
const StyledSuggestionsList = styled.ul`
  ${() => css`
    padding: 0;
    margin: 0;
    text-align: left;
    max-height: 380px;
    overflow-y: auto;

    li {
      padding: 0;
    }
  `}
`;
const StyledSuggestion = styled.button<{ active: boolean }>`
  ${({ active, theme: { colors, space } }) => css`
    width: 100%;
    background-color: ${active ? colors.grey2.default : colors.white.default};
    color: ${active ? colors.brand.default : colors.black.default};
    padding: ${space.sm} ${space.md};
    text-align: left;

    &:hover {
      background-color: ${colors.grey2.default};
      color: ${colors.brand.default};
    }
  `}
`;

export interface AutoCompleteSuggestionProps {
  name: string;
  value: string;
}
interface InputProps {
  suggestions?: AutoCompleteSuggestionProps[];
  handleAutoComplete: (value: string) => void;
  handleSelectValue: (value: string) => void;
  loading: boolean;
  defaultValue?: string;
  setValueOnChange?: boolean;
  placeholder?: string;
  noIcon?: boolean;
  errorMessage?: string;
}

export const AutoComplete = ({
  suggestions,
  handleAutoComplete,
  loading,
  defaultValue = '',
  handleSelectValue,
  setValueOnChange,
  placeholder,
  noIcon,
  errorMessage,
}: InputProps) => {

  // const [filteredSuggestions, setFilteredSuggestions] = useState<AutoCompleteSuggestionProps[] | []>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(0);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>();


  useEffect(() => {
    if(defaultValue) {
      setUserInput(defaultValue);
    }
  }, [defaultValue]);
  
  // On change to input
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value;


    setUserInput(userInput);
    // setFilteredSuggestions(unLinked);
    setActiveSuggestionIndex(0);
    setShowSuggestions(true);

    handleAutoComplete(userInput);

    // when there is no input pass it back up
    if (userInput === '') {
      handleSelectValue('');
    }
    // Set the input value on change
    if (setValueOnChange) {
      handleSelectValue(userInput);
    }
  };

  // Set active suggestion
  const onClick = (item) => {
    // setFilteredSuggestions([]);
    setUserInput(item.name);
    handleSelectValue(item.value);
    setActiveSuggestionIndex(0);
    setShowSuggestions(false);
  };

  // Keyboard support
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // User pressed the enter key
    if (e.key === 'Enter') {
      e.preventDefault();
      setUserInput(suggestions ? suggestions[activeSuggestionIndex]?.name : '');
      handleSelectValue(suggestions ? suggestions[activeSuggestionIndex]?.value : '');
      setActiveSuggestionIndex(0);
      setShowSuggestions(false);
    }
    // User pressed the up arrow
    else if (e.key === 'ArrowUp') {
      if (activeSuggestionIndex === 0) {
        return;
      }

      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    }
    // User pressed the down arrow
    else if (e.key === 'ArrowDown') {
      if (activeSuggestionIndex - 1 === suggestions?.length) {
        return;
      }

      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    }
  };

  const SuggestionsListComponent = ({ loading: isLoading }) => (
    <StyledSuggestions>
      {isLoading && (
        <StyledLoader>
          <Loader size="default" centered />
        </StyledLoader>
      )}

      {suggestions && suggestions?.length >= 1 ? (
        <StyledSuggestionsList>
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.name}>
              <StyledSuggestion
                type="button"
                onClick={() => onClick(suggestion)}
                active={index === activeSuggestionIndex}
              >
                {suggestion.name}
              </StyledSuggestion>
            </li>
          ))}
        </StyledSuggestionsList>
      ) : (
        <>{errorMessage && userInput.length > 2 ? <ErrorMessage>{errorMessage}</ErrorMessage> : ''}</>
      )}
    </StyledSuggestions>
  );

  return (
    <>
      <StyledAutoComplete>
        {!noIcon && <StyledSearchIcon />}
        <StyledInput
          type="search"
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={userInput}
          placeholder={placeholder}
          noIcon={noIcon}
        />
        {((showSuggestions && userInput) || (loading && userInput)) && <SuggestionsListComponent loading={loading} />}
      </StyledAutoComplete>
    </>
  );
};
